// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html"
// Establish Phoenix Socket and LiveView configuration.
import {Socket} from "phoenix"
import {LiveSocket} from "phoenix_live_view"
import topbar from "../vendor/topbar"

// Import hooks
import AppShellIsland from "./hooks/app_shell_island"
import AutoResize from "./hooks/auto_resize"
import AutoScroll from "./hooks/auto_scroll"
import Clipboard from "./hooks/clipboard"
import ECharts from "./hooks/echarts"
import ToolEditor from "./hooks/tool_editor"
import BrandStylesheetEditor from "./hooks/brand_stylesheet_editor"
import AvatarUpload from "./hooks/avatar_upload"
import MatthewDrawer from "./hooks/matthew_drawer"
import SupportChat from "./hooks/support_chat"
import ResizablePanel from "./hooks/resizable_panel"
import WsMonitor from "./hooks/ws_monitor"
import WebAuthn from "./hooks/webauthn"
import KanbanDrag from "./hooks/kanban_drag"
import AutoDismiss from "./hooks/auto_dismiss"
import DeckScale from "./hooks/deck_scale"
import AppEditor from "./hooks/app_editor"
import SortableTabs from "./hooks/sortable_tabs"
import SortableCockpit from "./hooks/sortable_cockpit"
import SortableList from "./hooks/sortable_list"
import FileTreeDrag from "./hooks/file_tree_drag"
import ResizeHandle from "./hooks/resize_handle"
import ChatMention from "./hooks/chat_mention"
import ChatSubmitClear from "./hooks/chat_submit_clear"
import DrawerMention from "./hooks/drawer_mention"
import SkillMention from "./hooks/skill_mention"
import LiveDuration from "./hooks/live_duration"
import ColorSync from "./hooks/color_sync"
import ChatOpenSignal from "./hooks/chat_open_signal"
import WorkflowEditorBridge from "./hooks/workflow_editor_bridge"
import DiscoveryBoardBridge from "./hooks/discovery_board_bridge"
import ErDiagramBridge from "./hooks/er_diagram_bridge"
import FlowMention from "./hooks/flow_mention"
import ButtonLoading from "./hooks/button_loading"
import Skeleton from "./hooks/skeleton"
import SignedUpload from "./hooks/signed_upload"
import IframeErrorBridge from "./hooks/iframe_error_bridge"
import OnboardingTour from "./hooks/onboarding_tour"
import Download from "./hooks/download"
import PdfDownload from "./hooks/pdf_download"
import PresentationEditorBridge from "./hooks/presentation_editor_bridge"
import LoginHero from "./hooks/login_hero"
import ThemeSelector from "./hooks/theme_selector"
import { EditLockHeartbeat } from "./hooks/edit_lock_heartbeat"
import { WidgetIframeAutoresize } from "./hooks/widget_iframe_autoresize"
import AudioRecorder from "./hooks/audio_recorder"
import DblClick from "./hooks/dbl_click"
import CommitteeShellNavigation from "./hooks/committee_shell_navigation"
import CommitteeResourceFrame from "./hooks/committee_resource_frame"
import CommitteeMonicaLauncher from "./hooks/committee_monica_launcher"
import SearchableSelect from "./hooks/searchable_select"
import SearchableSelectPanel from "./hooks/searchable_select_panel"
import ConsentWorkspaceSelect from "./hooks/consent_workspace_select"
import MarketplaceProvisioning from "./hooks/marketplace_provisioning"
// Side-effect import: sets up dark mode listeners (no hook registration).
import "./hooks/theme_sync"

import { t } from "./i18n.js"
let csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content")
// Embed mode uses a separate cookie and a separate LiveView socket so its
// session is isolated from the standalone Seiva app. The embed_root layout
// emits a `seiva-embed` meta tag; the path prefix is no longer a reliable
// signal now that embed routes share the standalone tree.
let socketPath = document.querySelector("meta[name='seiva-embed']") ? "/embed-live" : "/live"
let liveSocket = new LiveSocket(socketPath, Socket, {
  // Give slow mounts (e.g. CommitteeRoom) more time to establish the WebSocket
  // before falling back to longpoll. Longpoll causes repeated remounts, which
  // in turn re-initializes the Matthew drawer and other stateful components.
  longPollFallbackMs: 30000,
  params: {_csrf_token: csrfToken},
  hooks: {
    AutoResize,
    AutoScroll,
    Clipboard,
    ECharts,
    ToolEditor,
    BrandStylesheetEditor,
    AvatarUpload,
    AppShellIsland,
    MatthewDrawer,
    SupportChat,
    ResizablePanel,
    WsMonitor,
    WebAuthn,
    KanbanDrag,
    AutoDismiss,
    DeckScale,
    AppEditor,
    SortableTabs,
    SortableCockpit,
    SortableList,
    FileTreeDrag,
    ResizeHandle,
    ChatMention,
    ChatSubmitClear,
    DrawerMention,
    SkillMention,
    LiveDuration,
    ColorSync,
    ChatOpenSignal,
    WorkflowEditorBridge,
    DiscoveryBoardBridge,
    ErDiagramBridge,
    FlowMention,
    ButtonLoading,
    Skeleton,
    SignedUpload,
    IframeErrorBridge,
    OnboardingTour,
    Download,
    PdfDownload,
    PresentationEditorBridge,
    LoginHero,
    ThemeSelector,
    EditLockHeartbeat,
    WidgetIframeAutoresize,
    AudioRecorder,
    DblClick,
    CommitteeShellNavigation,
    CommitteeResourceFrame,
    CommitteeMonicaLauncher,
    SearchableSelect,
    SearchableSelectPanel,
    ConsentWorkspaceSelect,
    MarketplaceProvisioning,
  }
})

// O espelho da Documentação dentro do frame — ver o listener de clique abaixo.
const DOCS_FRAME_PATH = /^(?:\/embed)?\/committee\/builders\/resources\/docs(?:\/|$)/

// A Committee resource/editor is a regular LiveView inside a same-origin
// iframe. Phoenix live navigation updates that iframe's history, so report
// every location change to the parent shell. The server owns the mapping from
// internal/legacy paths to canonical `/committee/...` URLs.
function installCommitteeFrameNavigationBridge() {
  if (window.self === window.parent) return

  try {
    if (!window.parent.location.pathname.startsWith("/committee")) return
    if (
      !window.parent.document.getElementById("committee-workspace-shell") &&
      !window.parent.document.getElementById("committee-room-shell")
    ) return
  } catch (_error) {
    return
  }

  const notifyParent = () => {
    window.parent.postMessage(
      {
        type: "seiva:committee:navigate",
        path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      },
      window.location.origin,
    )
  }

  // The parent shell owns ALL browser history for committee navigation. The
  // frame is a puppet and must never grow its own joint-history entries — a
  // single user action stacking 2-3 entries is what desyncs the Back button and
  // strands the frame on a chromeless/full-chrome state. So convert the frame's
  // own pushState into replaceState (LiveView still swaps content; it just stops
  // adding history) and report the resulting location to the parent, which
  // writes the single canonical top-window history entry.
  const originalReplaceState = window.history.replaceState.bind(window.history)

  window.history.pushState = function (...args) {
    const result = originalReplaceState(...args)
    queueMicrotask(notifyParent)
    return result
  }
  window.history.replaceState = function (...args) {
    const result = originalReplaceState(...args)
    queueMicrotask(notifyParent)
    return result
  }

  document.addEventListener("click", event => {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest("a[href]")
    if (!link) return
    if (link.target === "_blank" || link.hasAttribute("download")) return

    const destination = new URL(link.href, window.location.origin)
    if (destination.origin !== window.location.origin) return
    if (
      destination.pathname !== "/committee" &&
      !destination.pathname.startsWith("/committee/")
    ) return

    // A doc é o único recurso que navega DENTRO de si: índice, categoria e
    // página são três rotas do MESMO live_session, e o sumário linka de uma
    // para a outra. Entregar esses cliques ao pai quebra a Sala — lá o pai só
    // RECONCILIA estado (a URL pública é a do Projeto, não a do recurso) e não
    // tem para onde dirigir o frame, então o clique morria no preventDefault
    // abaixo e a doc ficava presa na página principal. Deixando o frame
    // navegar sozinho, o pushState logo acima já avisa o pai: o Cockpit segue
    // escrevendo `/committee/docs/...` na URL, e sem recarregar o iframe,
    // porque ele já está no destino quando o drive chega.
    if (DOCS_FRAME_PATH.test(destination.pathname)) return

    event.preventDefault()
    event.stopImmediatePropagation()
    window.parent.postMessage(
      {
        type: "seiva:committee:navigate",
        path: `${destination.pathname}${destination.search}${destination.hash}`,
      },
      window.location.origin,
    )
  }, true)

  // Intentionally NO popstate / phx:page-loading-stop listeners and no initial
  // notify: Back/Forward belong to the parent's top window, which re-drives the
  // frame via contentWindow.location.replace on its own popstate. Notifying here
  // would echo the parent's own drives back at it and re-pollute joint history.
}

installCommitteeFrameNavigationBridge()

// Embed navigation bridge — embed sessions (meta `seiva-embed`, emitted by
// the embed_root layout) run inside a cross-origin partner iframe, so the
// host page cannot read the frame's location. Report every navigation
// instead: the Accountfy shell uses it to position its own chrome (the
// floating home button) per surface. Unlike the committee bridge above, the
// frame owns its history here — pushState is left untouched and popstate is
// reported too. Hosts that do not listen simply ignore the message.
function installEmbedNavigationBridge() {
  if (window.self === window.parent) return
  if (!document.querySelector("meta[name='seiva-embed']")) return

  const targetOrigin = () => {
    try {
      return new URL(document.referrer).origin
    } catch (_error) {
      return "*"
    }
  }

  const notifyParent = () => {
    window.parent.postMessage(
      {
        type: "seiva:embed:navigate",
        path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      },
      targetOrigin(),
    )
  }

  const originalPushState = window.history.pushState.bind(window.history)
  const originalReplaceState = window.history.replaceState.bind(window.history)

  window.history.pushState = function (...args) {
    const result = originalPushState(...args)
    queueMicrotask(notifyParent)
    return result
  }
  window.history.replaceState = function (...args) {
    const result = originalReplaceState(...args)
    queueMicrotask(notifyParent)
    return result
  }

  window.addEventListener("popstate", notifyParent)
  window.addEventListener("hashchange", notifyParent)

  // Botão nativo do rail (data-seiva-embed-home) pede ao host para voltar à
  // home do parceiro. Delegado no document porque o LiveView re-renderiza o
  // rail via morphdom — o listener sobrevive às trocas de nó.
  document.addEventListener("click", event => {
    if (!(event.target instanceof Element)) return
    if (!event.target.closest("[data-seiva-embed-home]")) return
    window.parent.postMessage({ type: "seiva:embed:go-home" }, targetOrigin())
  })

  notifyParent()
}

installEmbedNavigationBridge()

// Google consent must replace the Committee shell, never the resource iframe.
// The destination is deliberately restricted to the same-origin initiation
// endpoint; the controller then performs the external redirect to Google.
window.addEventListener("phx:google_oauth:top_navigate", event => {
  const destination = event.detail?.to
  if (typeof destination !== "string") return

  try {
    const url = new URL(destination, window.location.origin)
    if (url.origin !== window.location.origin) return
    if (url.pathname !== "/auth/google/initiate") return

    window.top.location.assign(url.toString())
  } catch (_error) {
    // Ignore malformed destinations instead of navigating the parent window.
  }
})

// Show progress bar on live navigation and form submits. Committee resources
// run the same LiveView client inside a same-origin iframe, but the outer shell
// owns navigation feedback. Suppress only the frame's page-level bar so the
// shell and the embedded resource do not render stacked progress indicators.
function isEmbeddedCommitteeSurface() {
  return (
    window.self !== window.parent &&
    new URLSearchParams(window.location.search).get("committee_surface") === "true"
  )
}

// The Monica/Support launchers of a Committee resource belong to the PARENT
// document and float over this frame's lower-right corner — exactly where a
// side drawer pins its footer. `app.css` reads this class to keep the footer
// actions clear of them (no z-index inside the frame can lift above them).
if (isEmbeddedCommitteeSurface()) {
  document.documentElement.classList.add("seiva-committee-frame")
}

topbar.config({barColors: {0: "#FF826B"}, shadowColor: "rgba(3, 47, 83, .24)"})
window.addEventListener("phx:page-loading-start", _info => {
  if (isEmbeddedCommitteeSurface()) {
    topbar.hide()
    return
  }

  topbar.show(300)
})
window.addEventListener("phx:page-loading-stop", _info => {
  if (!isEmbeddedCommitteeSurface()) topbar.hide()
})

window.addEventListener("phx:branding_cache_reset", (event) => {
  try {
    const workspaceId = event.detail && event.detail.workspace_id
    const prefixes = ["seiva_branding", "seiva_style_vars", "seiva_color_tokens"]

    Object.keys(localStorage).forEach((key) => {
      if (prefixes.some((prefix) => key === prefix || key.startsWith(`${prefix}:`) || key.startsWith(`${prefix}_`))) {
        localStorage.removeItem(key)
      }

      if (workspaceId && key.includes(workspaceId) && /brand|style|color/i.test(key)) {
        localStorage.removeItem(key)
      }
    })
  } catch (_error) {
    // Ignore storage errors; the reload below still refreshes server-rendered branding.
  }

  window.location.reload()
})

// Scroll a target element into view. Used when collapsing the PRD card so the
// user lands on the now-collapsed header instead of empty space below, and by
// the Statements report nav (DRE / BP / DFC), whose targets sit inside the
// section's own scroll port rather than the document.
window.addEventListener("phx:scroll_into_view", e => {
  const id = e.detail && e.detail.id
  if (!id) return
  requestAnimationFrame(() => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
  })
})

// Set credential type search field value (used after creating a custom credential type)
window.addEventListener("phx:set_type_field_value", e => {
  const value = e.detail && e.detail.value
  if (!value) return
  requestAnimationFrame(() => {
    const el = document.querySelector('[data-testid="credentials-type-search"]')
    if (el) el.value = value
  })
})

// Focus on a target element (used after creating credential type to focus on Name field)
window.addEventListener("phx:focus", e => {
  const selector = e.detail && e.detail.to
  if (!selector) return
  requestAnimationFrame(() => {
    const el = document.querySelector(selector)
    if (el) el.focus()
  })
})

// A cópia vem do catálogo Gettext (via `#seiva-i18n`), não de um switch por
// `lang` — três idiomas embutidos aqui divergiam do resto da plataforma.
function confirmCopy() {
  return { title: t("Confirm action"), cancel: t("Cancel"), confirm: t("Confirm") }
}

function closePlatformConfirm(modal, restoreFocus) {
  modal.remove()
  if (restoreFocus && typeof restoreFocus.focus === "function") restoreFocus.focus()
}

function escapeHtml(value) {
  const div = document.createElement("div")
  div.textContent = value || ""
  return div.innerHTML
}

function openPlatformConfirm(message, onConfirm, restoreFocus) {
  const copy = confirmCopy()
  const modal = document.createElement("div")
  // The featured SMALL frame is the platform's current modal standard
  // (radius-sm corners, gradient hairline) — the confirm must look like a
  // sibling of every other dialog, not like the sheet it once was.
  modal.className = "seiva-modal-root flex items-end sm:items-center justify-center p-0 sm:p-4"
  // A confirmation is the one modal that stays centered; every content modal
  // opens as the side drawer (see `.seiva-modal-root` in app.css).
  modal.dataset.placement = "center"
  const safeMessage = escapeHtml(message)
  modal.innerHTML = `
    <div class="seiva-modal-overlay" data-confirm-cancel></div>
    <div class="seiva-modal-featured-frame seiva-modal-featured-frame--sm seiva-modal-panel--sm" role="dialog" aria-modal="true" aria-labelledby="platform-confirm-title">
      <div class="seiva-modal-featured-panel">
        <button type="button" class="seiva-modal-close absolute top-3 right-3 z-10" data-confirm-cancel aria-label="${copy.cancel}">
          <span aria-hidden="true">&times;</span>
        </button>
        <h3 id="platform-confirm-title" class="pr-8 text-sm font-semibold text-[var(--text-primary)]">${copy.title}</h3>
        <p class="mt-2 text-sm text-[var(--text-secondary)]">${safeMessage}</p>
        <div class="mt-5 flex justify-end gap-2">
          <button type="button" class="seiva-btn seiva-btn--secondary seiva-btn-size--md" data-confirm-cancel>${copy.cancel}</button>
          <button type="button" class="seiva-btn seiva-btn--danger seiva-btn-size--md" data-confirm-accept>${copy.confirm}</button>
        </div>
      </div>
    </div>
  `

  const onCancel = () => closePlatformConfirm(modal, restoreFocus)
  const onKeydown = event => {
    if (event.key === "Escape") onCancel()
  }

  modal.addEventListener("click", event => {
    if (event.target.closest("[data-confirm-cancel]")) onCancel()
    if (event.target.closest("[data-confirm-accept]")) {
      closePlatformConfirm(modal, restoreFocus)
      onConfirm()
    }
  })
  modal.addEventListener("keydown", onKeydown)
  document.body.appendChild(modal)
  modal.querySelector("[data-confirm-accept]")?.focus()
}

document.addEventListener("click", event => {
  const target = event.target.closest("[data-confirm]")
  if (!target || target.dataset.platformConfirmSkip === "true") return

  const message = target.getAttribute("data-confirm")
  if (!message) return

  event.preventDefault()
  event.stopImmediatePropagation()

  openPlatformConfirm(message, () => {
    const original = target.getAttribute("data-confirm")
    target.dataset.platformConfirmSkip = "true"
    target.removeAttribute("data-confirm")
    target.click()
    window.setTimeout(() => {
      target.setAttribute("data-confirm", original)
      delete target.dataset.platformConfirmSkip
    }, 0)
  }, target)
}, true)

// connect if there are any LiveViews on the page
liveSocket.connect()

// expose liveSocket on window for web console debug logs and latency simulation:
window.liveSocket = liveSocket

// ===== Sidebar collapse toggle =====
// Toggles between w-60 (expanded) and w-20 (collapsed) without a server round-trip.
// The sidebar uses CSS to animate labels and chevrons; JS only switches width.
window.addEventListener("seiva:toggle-sidebar", () => {
  const sidebar = document.getElementById("app-sidebar")
  const toggle = document.getElementById("sidebar-toggle")
  if (!sidebar) return

  const isCollapsed = sidebar.classList.contains("w-20")

  if (isCollapsed) {
    sidebar.classList.remove("w-20")
    sidebar.classList.add("w-60")
    document.documentElement.classList.add("seiva-sidebar-expanded")
    if (toggle) toggle.setAttribute("aria-label", t("Collapse sidebar"))
    localStorage.setItem("seiva_sidebar_collapsed", "false")
  } else {
    sidebar.classList.remove("w-60")
    sidebar.classList.add("w-20")
    document.documentElement.classList.remove("seiva-sidebar-expanded")
    if (toggle) toggle.setAttribute("aria-label", t("Expand sidebar"))
    localStorage.setItem("seiva_sidebar_collapsed", "true")
  }
})

// Sidebar always starts collapsed (w-20 in HTML) — no localStorage restore
document.documentElement.classList.remove("seiva-sidebar-expanded")

// ===== Mobile off-canvas sidebar drawer =====
// Hamburger button uses JS.add_class directly (in HEEx) for the open state.
// These listeners handle automatic closing: backdrop click (also via JS.remove_class),
// Esc key, navigation, sidebar nav link click, and viewport growing past lg.
function closeMobileSidebar() {
  const sidebar = document.getElementById("app-sidebar") || document.getElementById("partnership-sidebar")
  const backdrop = document.getElementById("sidebar-backdrop")
  if (sidebar) sidebar.classList.remove("mobile-open")
  if (backdrop) backdrop.classList.remove("visible")
  document.body.classList.remove("overflow-hidden")
}

// Esc key closes the mobile drawer
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") closeMobileSidebar()
})

// Auto-close on navigation (LiveView page transitions)
window.addEventListener("phx:page-loading-stop", () => closeMobileSidebar())

// Auto-close when user taps any sidebar nav link (so SPA-style nav closes the drawer)
document.addEventListener("click", (e) => {
  const sidebar = document.getElementById("app-sidebar") || document.getElementById("partnership-sidebar")
  if (!sidebar || !sidebar.classList.contains("mobile-open")) return
  const link = e.target.closest("a[href], a[data-phx-link]")
  if (link && sidebar.contains(link)) closeMobileSidebar()
})

// Auto-close when viewport grows past lg breakpoint
const lgMedia = window.matchMedia("(min-width: 1024px)")
const onLgChange = (e) => { if (e.matches) closeMobileSidebar() }
if (lgMedia.addEventListener) lgMedia.addEventListener("change", onLgChange)
else if (lgMedia.addListener) lgMedia.addListener(onLgChange)

// ===== Sidebar collapsible sections (accordion) =====
window.toggleSidebarSection = function(section) {
  const content = document.querySelector(`[data-section-content="${section}"]`)
  const chevron = document.querySelector(`[data-chevron="${section}"]`)
  if (!content) return

  const isCollapsed = content.classList.contains("collapsed")

  if (isCollapsed) {
    content.classList.remove("collapsed")
    content.classList.add("expanded")
    content.style.maxHeight = content.scrollHeight + "px"
    if (chevron) chevron.classList.remove("rotated")
  } else {
    content.style.maxHeight = content.scrollHeight + "px"
    // Force reflow
    content.offsetHeight
    content.classList.add("collapsed")
    content.classList.remove("expanded")
    content.style.maxHeight = "0px"
    if (chevron) chevron.classList.add("rotated")
  }

  // Persist state
  const collapsed = JSON.parse(localStorage.getItem("seiva_sidebar_sections") || "{}")
  collapsed[section] = !isCollapsed
  localStorage.setItem("seiva_sidebar_sections", JSON.stringify(collapsed))
}

// Restore collapsed sidebar sections on page load
document.addEventListener("DOMContentLoaded", () => {
  const collapsed = JSON.parse(localStorage.getItem("seiva_sidebar_sections") || "{}")
  Object.entries(collapsed).forEach(([section, isCollapsed]) => {
    if (isCollapsed) {
      const content = document.querySelector(`[data-section-content="${section}"]`)
      const chevron = document.querySelector(`[data-chevron="${section}"]`)
      if (content) {
        content.classList.add("collapsed")
        content.classList.remove("expanded")
        content.style.maxHeight = "0px"
      }
      if (chevron) chevron.classList.add("rotated")
    }
  })
})
